import React, { Component } from 'react';

import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import { navigate, RouteComponentProps } from '@reach/router';

import { Consumer } from '../../../../store';
import { IAuthActions } from '../../../../store/auth/actions';
import { IAuthStore } from '../../../../store/auth/store';
import { INotificationsActions } from '../../../../store/notification/actions';
import { IMessageNotification } from '../../../../store/notification/store';

import Button from '../../../../components/Button';
import { FormConsumer, FormProvider, IFormAttributes } from '../../../../components/Forms';
import Input from '../../../../components/Input';
import { withContext } from '../../../../contrib/utils';
import { externalRoutes } from '../../../../routes/Utils/Routes/external.routes';

interface IState {
  invalidEmail: boolean;
  invalidEmailMessage: string;
  invalidPassword: boolean;
  invalidPasswordMessage: string;
}

interface IProps extends RouteComponentProps {
  actions: { auth: IAuthActions; notifications: INotificationsActions };
  invitationId?: string;
}

class CreateAccountComponent extends Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      invalidEmail: false,
      invalidEmailMessage: '',
      invalidPassword: false,
      invalidPasswordMessage: ''
    };

    this.onSubmit = this.onSubmit.bind(this);
  }

  public onSubmit(): (data: IAuthStore) => Promise<void> {
    return async (data: IAuthStore) => {
      const response = await this.props.actions.auth.register(data);
      if (response.errors) {
        await this.props.actions.notifications.push(({
          status: 'unread',
          text: response.errors.auth.msg,
          type: 'error'
        } as unknown) as IMessageNotification);
      }
      if (response.errors && response.errors.auth && response.errors.auth.el === 'password1') {
        this.setState({
          invalidPassword: true,
          invalidPasswordMessage: response.errors.auth.msg || 'Error'
        });
      }
      if (response.errors && response.errors.auth && response.errors.auth.el === 'email') {
        this.setState({ invalidEmail: true, invalidEmailMessage: response.errors.auth.msg || '' });
      }
      if (response.auth && response.auth.isAuthenticated) {
        navigate(`/staff/club/create/info`);
      }
    };
  }

  public render(): JSX.Element {
    return (
      <Consumer>
        {({ auth }) => (
          <div>
            <FormProvider
              defaultValues={{ ...auth, acceptedToS: false, user: { ...auth.user } }}
              onSubmit={this.onSubmit()}
              validations={{
                acceptedToS: 'isTrue',
                password1: 'password',
                password2: values => values.password1 === values.password2,
                'user.email': 'required',
                'user.firstName': 'required',
                'user.lastName': 'required'
              }}
            >
              <FormConsumer>
                {({ values, formActions: { setValue, toggleValue }, errors }: IFormAttributes) => (
                  <div className="create-account form">
                    <h1 className="create-account__title override">WHO RUNS YOUR CLUB?</h1>
                    <p className="help-text">
                      This person will run the show. Don't worry, you can always transfer this
                      responsibility to someone else.
                    </p>
                    <Input
                      label="FIRST NAME"
                      id="first-name"
                      type="text"
                      value={values.user.firstName}
                      onChange={setValue('user.firstName')}
                      hasError={errors['user.firstName']}
                      errorMessage="First Name cannot be empty"
                      fullWidth={true}
                    />

                    <Input
                      label="LAST NAME"
                      id="last-name"
                      type="text"
                      value={values.user.lastName}
                      onChange={setValue('user.lastName')}
                      hasError={errors['user.lastName']}
                      errorMessage="Last Name cannot be empty"
                      fullWidth={true}
                    />

                    <Input
                      label="EMAIL"
                      id="email"
                      type="email"
                      value={values.user.email}
                      onChange={setValue('user.email')}
                      hasError={errors['user.email'] || this.state.invalidEmail}
                      errorMessage={
                        this.state.invalidEmailMessage || 'You must provide a valid email'
                      }
                      fullWidth={true}
                    />

                    <Input
                      label="PASSWORD"
                      id="password1"
                      type="password"
                      onChange={setValue('password1')}
                      hasError={errors.password1 || this.state.invalidPassword}
                      errorMessage={
                        this.state.invalidPassword
                          ? this.state.invalidPasswordMessage
                          : 'This password is not strong enough'
                      }
                      fullWidth={true}
                    />

                    <Input
                      label="CONFIRM PASSWORD"
                      id="password2"
                      type="password"
                      onChange={setValue('password2')}
                      hasError={errors.password2}
                      errorMessage="Passwords must match"
                      fullWidth={true}
                    />

                    <FormControlLabel
                      classes={{ label: 'tos-checkbox-label-staff override' }}
                      control={
                        <Checkbox
                          checkedIcon={<CheckBoxIcon className="form-icon override" />}
                          color="primary"
                          icon={<CheckBoxOutlineBlankIcon className="form-icon override" />}
                          id="accepted-tos"
                          onChange={toggleValue('acceptedToS')}
                          value="on"
                        />
                      }
                      label={
                        <>
                          Check this box to confirm that you have read and understand our terms and
                          conditions including the&nbsp;
                          <a href={externalRoutes.termsOfService} target="_blank">
                            Terms of Service
                          </a>
                          &nbsp;and&nbsp;
                          <a href={externalRoutes.privacyPolicy} target="_blank">
                            Privacy Policy
                          </a>
                        </>
                      }
                    />

                    <Button
                      className="create-account__next-button button--shadow"
                      color="primary"
                      disabled={errors.FORM}
                      type="submit"
                      size="large"
                      variant="contained"
                    >
                      NEXT
                    </Button>
                  </div>
                )}
              </FormConsumer>
            </FormProvider>
          </div>
        )}
      </Consumer>
    );
  }
}

const CreateAccount = withContext(CreateAccountComponent, 'auth', 'notifications');

export { CreateAccount };
